/**
 * Reine Hilfsfunktionen der Gelegenheitsliste: Formatierung, Aufbereitung fremder Dossier-Inhalte
 * und die Einordnung des Barometers über den Rang in der eigenen Geschichte.
 */

export const DASH = '—';

export const FALLBACK_DISCLAIMER = 'Arbeitsliste zur eigenen Prüfung ohne Renditebeleg. Keine Anlageempfehlung.';

export const RATING_LABELS = { interesting: 'Interessant', rejected: 'Verworfen', bought: 'Gekauft' };

export const YIELD_MEASURES = {
    fcf_after_sbc: { short: 'FCF n. Vergütung', long: 'Free-Cashflow-Rendite nach Aktienvergütung', field: 'fcf_after_sbc_yield' },
    fcf_unadjusted: { short: 'FCF unbereinigt', long: 'Free-Cashflow-Rendite ohne Abzug der Aktienvergütung', field: 'fcf_yield' },
    earnings: { short: 'Gewinnrendite', long: 'Gewinnrendite', field: 'earnings_yield' },
};

export const SBC_NOTES = {
    missing_current: 'Aktienvergütung fehlt in den letzten 4 Quartalen. Rendite und Median sind nicht darum bereinigt.',
    missing_history: 'Aktienvergütung fehlt in der Historie. Rendite und Median sind nicht darum bereinigt.',
};

export const DOSSIER_STATE_LABELS = {
    created: 'Neu erstellt',
    reused: 'Übernommen',
    no_sources: 'Keine Quellen',
    failed: 'Fehlgeschlagen',
    skipped_budget: 'Nicht erstellt',
};

/** Einordnung für die Prüfreihenfolge. Keine Kaufempfehlung. „rank“ sortiert „Genauer prüfen“ nach oben. */
export const FAZIT_LABELS = {
    genauer_pruefen: { label: 'Genauer prüfen', rank: 3 },
    zu_wenig_belegt: { label: 'Zu wenig belegt', rank: 2 },
    eher_aussortieren: { label: 'Eher aussortieren', rank: 1 },
};

export const FAZIT_HINT = 'KI-Einordnung für die Reihenfolge der eigenen Prüfung. Keine Kaufempfehlung und keine Aussage über künftige Kurse.';

/** „belegt“ heißt nur: Eine datierte Quelle nennt das. Es ist kein Nachweis der wahren Ursache. */
export const BELEG_LABELS = { belegt: 'datierte Quelle', teilweise: 'teilweise', unklar: 'unklar' };

export const BELEG_HINT = '„Datierte Quelle“ heißt: Eine Quelle mit passendem Datum nennt das. Ob es die wahre Ursache ist, belegt das nicht.';

const SOURCE_ORIGINS = { eod_news: 'EOD-Nachrichten', search_window: 'Suche im Zeitfenster', search: 'Suche' };

const ESTIMATE_PERIODS = { '0q': 'Laufendes Quartal', '+1q': 'Nächstes Quartal', '0y': 'Laufendes Jahr', '+1y': 'Nächstes Jahr' };

const HIT_FLAGS = {
    sbc_missing: 'unbereinigt',
    sbc_missing_history: 'unbereinigt',
    sbc_extrapolated: 'hochgerechnet',
    fcf_corrected: 'FCF korrigiert',
};

export const EINSCHAETZUNG_LABELS = {
    eher_voruebergehend: 'eher vorübergehend',
    eher_dauerhaft: 'eher dauerhaft',
    unklar: 'unklar',
};

export function toNumber(value) {
    if (value === null || value === undefined || value === '') return null;
    const number = Number(value);
    return Number.isFinite(number) ? number : null;
}

export function fmtNumber(value, digits = 1) {
    const number = toNumber(value);
    if (number === null) return DASH;
    return number.toLocaleString('de-DE', { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

export function fmtCount(value) {
    const number = toNumber(value);
    return number === null ? DASH : number.toLocaleString('de-DE', { maximumFractionDigits: 0 });
}

/** Renditen und Veränderungen kommen als Anteil. */
export function fmtPct(share, digits = 1) {
    const number = toNumber(share);
    return number === null ? DASH : `${fmtNumber(number * 100, digits)} %`;
}

export function fmtPctSigned(share, digits = 1) {
    const number = toNumber(share);
    if (number === null) return DASH;
    const percent = number * 100;
    const sign = percent > 0 && Number(percent.toFixed(digits)) !== 0 ? '+' : '';
    return `${sign}${fmtNumber(percent, digits)} %`;
}

export function fmtMultiple(value) {
    const number = toNumber(value);
    return number === null ? DASH : `${fmtNumber(number, 1)}-fach`;
}

export function fmtLarge(value, currency) {
    const number = toNumber(value);
    if (number === null) return DASH;
    const abs = Math.abs(number);
    let text;
    if (abs >= 1e12) text = `${fmtNumber(number / 1e12, 1)} Bio.`;
    else if (abs >= 1e9) text = `${fmtNumber(number / 1e9, 1)} Mrd.`;
    else if (abs >= 1e6) text = `${fmtNumber(number / 1e6, 0)} Mio.`;
    else text = fmtNumber(number, 0);
    return currency ? `${text} ${currency}` : text;
}

export function fmtPrice(value, currency) {
    const number = toNumber(value);
    if (number === null) return DASH;
    return currency ? `${fmtNumber(number, 2)} ${currency}` : fmtNumber(number, 2);
}

/** Tagesangaben ohne Zeitzonen-Verschiebung, Zeitstempel in lokaler Zeit. */
export function fmtDate(value) {
    if (!value) return DASH;
    const text = String(value);
    const day = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text);
    if (day) return `${day[3]}.${day[2]}.${day[1]}`;
    const parsed = new Date(text);
    if (Number.isNaN(parsed.getTime())) return DASH;
    return parsed.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/**
 * Serverseitige Hinweise schreiben Prozente mit Punkt und Daten als JJJJ-MM-TT.
 * Nur diese beiden Muster werden umgeschrieben, damit sonst nichts am Text verändert wird.
 */
export function germanizeText(text) {
    if (typeof text !== 'string') return '';
    return text
        .replace(/(\d)\.(\d+)(?=\s*%)/g, '$1,$2')
        .replace(/\b(\d{4})-(\d{2})-(\d{2})\b/g, '$3.$2.$1');
}

const ENTITIES = { '&amp;': '&', '&quot;': '"', '&#39;': "'", '&apos;': "'", '&lt;': '<', '&gt;': '>' };

/** Quellentitel enthalten teils HTML-Entitäten. Ausgegeben wird nur Text, deshalb ist das Auflösen unkritisch. */
export function decodeEntities(text) {
    return text.replace(/&(?:amp|quot|#39|apos|lt|gt);/g, (entity) => ENTITIES[entity]);
}

/** Liefert die Adresse nur bei http(s), alles andere gilt als nicht verlinkbar. */
export function safeHttpUrl(value) {
    if (typeof value !== 'string' || !value.trim()) return null;
    try {
        const url = new URL(value.trim());
        return url.protocol === 'http:' || url.protocol === 'https:' ? url.href : null;
    } catch (error) {
        return null;
    }
}

/** Kennungen ohne externe Quelle: Die Angabe stammt aus den eigenen Daten. */
export const INTERNAL_ORIGINS = { KENNZAHLEN: 'Kennzahlen', KURSDATEN: 'Kursdaten' };

const CITATION_ID = 'S\\d+|KENNZAHLEN|KURSDATEN';
const CITATION = new RegExp(`\\[\\s*((?:${CITATION_ID})(?:\\s*[,;]\\s*(?:${CITATION_ID}))*)\\s*\\]|\\b(${CITATION_ID})\\b`, 'g');

/**
 * Zerlegt Dossier-Text in Textstücke und Quellenkennungen, mit Klammer wie [S2] oder im Fließtext wie
 * „S2 meldet“. Ob eine Kennung wirklich eine Quelle ist, entscheidet erst die Quellenliste.
 */
export function splitCitations(text) {
    if (typeof text !== 'string' || !text) return [];
    const parts = [];
    let last = 0;
    for (const match of text.matchAll(CITATION)) {
        if (match.index > last) parts.push({ type: 'text', value: text.slice(last, match.index) });
        if (match[2]) {
            parts.push({ type: 'cite', id: match[2], bare: true });
        } else {
            match[1].split(/\s*[,;]\s*/).forEach((id) => parts.push({ type: 'cite', id }));
        }
        last = match.index + match[0].length;
    }
    if (last < text.length) parts.push({ type: 'text', value: text.slice(last) });
    return parts;
}

function normalizeEntry(raw) {
    if (typeof raw === 'string') {
        return raw.trim() ? { text: raw, quellen: [], belegt: null, einschaetzung: null } : null;
    }
    if (!raw || typeof raw !== 'object' || typeof raw.text !== 'string' || !raw.text.trim()) return null;
    return {
        text: raw.text,
        quellen: Array.isArray(raw.quellen) ? raw.quellen.filter((id) => typeof id === 'string') : [],
        belegt: BELEG_LABELS[raw.belegt] ? raw.belegt : null,
        einschaetzung: EINSCHAETZUNG_LABELS[raw.einschaetzung] ? raw.einschaetzung : null,
    };
}

function normalizeList(raw) {
    return Array.isArray(raw) ? raw.map(normalizeEntry).filter(Boolean) : [];
}

const DAY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

function isoDay(value) {
    const day = String(value || '').slice(0, 10);
    return DAY_PATTERN.test(day) ? day : null;
}

function parseRange(text) {
    if (typeof text !== 'string') return { von: null, bis: null, change: null };
    const date = '(\\d{4}-\\d{2}-\\d{2}|\\d{2}\\.\\d{2}\\.\\d{4})';
    const range = new RegExp(`${date}\\s*(?:bis|–)\\s*${date}`).exec(text);
    const toIso = (value) => (value.includes('.') ? value.split('.').reverse().join('-') : value);
    const percent = /\(\s*([+-]?\d+(?:[.,]\d+)?)\s*%\s*\)/.exec(text);
    return {
        von: range ? toIso(range[1]) : null,
        bis: range ? toIso(range[2]) : null,
        change: percent ? Number(percent[1].replace(',', '.')) / 100 : null,
    };
}

/**
 * Führt die Rückgangsphasen aus den Kursdaten mit den Erklärungen des Dossiers zusammen, zeitlich sortiert.
 * Eine Phase ohne Erklärung bleibt sichtbar, eine Erklärung ohne passende Phase ebenfalls.
 */
export function buildDeclineTimeline(content) {
    const phases = Array.isArray(content?.kursrueckgang?.phasen)
        ? content.kursrueckgang.phasen.filter((phase) => isoDay(phase?.von) && isoDay(phase?.bis))
        : [];
    const usedPhases = new Set();
    const rows = (Array.isArray(content?.rueckgaenge) ? content.rueckgaenge : []).map((raw) => {
        const entry = normalizeEntry(raw);
        if (!entry) return null;
        const range = parseRange(raw.zeitraum);
        const phase = phases.find((candidate) => candidate.von === range.von && candidate.bis === range.bis);
        if (phase) usedPhases.add(phase);
        return {
            von: range.von,
            bis: range.bis,
            label: range.von ? null : (typeof raw.zeitraum === 'string' ? raw.zeitraum : null),
            change: phase ? toNumber(phase.veraenderung) : range.change,
            monthly: phase?.aufloesung === 'monthly',
            entry,
        };
    }).filter(Boolean);
    phases.filter((phase) => !usedPhases.has(phase)).forEach((phase) => {
        rows.push({
            von: phase.von,
            bis: phase.bis,
            label: null,
            change: toNumber(phase.veraenderung),
            monthly: phase.aufloesung === 'monthly',
            entry: null,
        });
    });
    return rows.sort((a, b) => {
        if (!a.von || !b.von) return a.von ? -1 : 1;
        return a.von.localeCompare(b.von);
    });
}

function normalizeDayLosses(facts) {
    if (!Array.isArray(facts?.groesste_tagesverluste)) return [];
    return facts.groesste_tagesverluste
        .filter((row) => isoDay(row?.datum) && toNumber(row?.veraenderung) !== null)
        .map((row) => ({
            datum: isoDay(row.datum),
            veraenderung: toNumber(row.veraenderung),
            termin: isoDay(row.berichtstermin_in_der_naehe),
        }));
}

function normalizeEarnings(raw) {
    if (!Array.isArray(raw)) return [];
    return raw
        .filter((row) => isoDay(row?.termin))
        .map((row) => ({
            termin: isoDay(row.termin),
            quartal: isoDay(row.quartal),
            eps: toNumber(row.eps),
            erwartet: toNumber(row.eps_erwartet),
            ueberraschung: toNumber(row.ueberraschung_prozent),
            kurs2Tage: toNumber(row.kursbewegung_2_tage),
        }))
        .sort((a, b) => b.termin.localeCompare(a.termin));
}

function normalizeEstimates(raw) {
    if (!Array.isArray(raw)) return [];
    return raw
        .filter((row) => row && typeof row === 'object' && typeof row.zeitraum === 'string')
        .map((row) => ({
            zeitraum: ESTIMATE_PERIODS[row.zeitraum] || row.zeitraum,
            stand: isoDay(row.stand),
            schaetzung: toNumber(row.schaetzung),
            aenderung30: toNumber(row.aenderung_30_tage),
            aenderung90: toNumber(row.aenderung_90_tage),
            hoch: toNumber(row.revisionen_hoch_30_tage),
            runter: toNumber(row.revisionen_runter_30_tage),
        }));
}

function normalizePriceCoverage(facts) {
    const daily = isoDay(facts?.tageskurse_bis);
    const all = isoDay(facts?.kurse_bis);
    return daily && all && daily < all ? { daily, all } : null;
}

/** Vorzeichen für Werte, die schon in Prozent vorliegen, etwa die EPS-Überraschung. */
export function fmtPercentValueSigned(value, digits = 1) {
    const number = toNumber(value);
    if (number === null) return DASH;
    return `${number > 0 ? '+' : ''}${fmtNumber(number, digits)} %`;
}

/**
 * Kennzeichnungen am Treffer, wenn die Rendite nicht ohne Weiteres mit anderen vergleichbar ist:
 * fehlende oder hochgerechnete Aktienvergütung, korrigierter Free Cashflow.
 */
export function hitFlags(hit) {
    const reasons = Array.isArray(hit?.reasons) ? hit.reasons : [];
    const flags = reasons
        .filter((reason) => HIT_FLAGS[reason?.code])
        .map((reason) => ({ code: reason.code, label: HIT_FLAGS[reason.code], text: germanizeText(reason.text) }));
    if (SBC_NOTES[hit?.sbc_status] && !flags.some((flag) => flag.label === 'unbereinigt')) {
        flags.unshift({ code: hit.sbc_status, label: 'unbereinigt', text: SBC_NOTES[hit.sbc_status] });
    }
    return flags;
}

export function isFlagReason(reason) {
    return Boolean(HIT_FLAGS[reason?.code]);
}

/** Nimmt aus dem Dossier nur die bekannten Felder. Unbekannte Felder werden nicht angezeigt. */
function normalizeFazit(fazit) {
    const einordnung = fazit && typeof fazit === 'object' ? fazit.einordnung : null;
    if (!FAZIT_LABELS[einordnung]) return null;
    return { einordnung, ...normalizeEntry({ text: fazit.text, quellen: fazit.quellen }) };
}

export function normalizeDossierContent(content) {
    const source = content && typeof content === 'object' ? content : {};
    return {
        warum: normalizeEntry(source.warum_gefallen),
        dauer: normalizeEntry(source.voruebergehend_oder_dauerhaft),
        dagegen: normalizeList(source.was_dagegen_spricht),
        widerlegen: normalizeList(source.was_die_idee_widerlegen_wuerde),
        unklar: normalizeList(source.unklar),
        fazit: normalizeFazit(source.fazit),
        hinweis: typeof source.hinweis === 'string' && source.hinweis.trim() ? source.hinweis : null,
        zeitleiste: buildDeclineTimeline(source),
        tagesverluste: normalizeDayLosses(source.kursrueckgang),
        berichtstermine: normalizeEarnings(source.berichtstermine),
        schaetzungen: normalizeEstimates(source.schaetzungsaenderungen),
        kursstand: normalizePriceCoverage(source.kursrueckgang),
    };
}

export function normalizeSources(sources) {
    if (!Array.isArray(sources)) return [];
    return sources
        .filter((source) => source && typeof source.id === 'string')
        .map((source) => ({
            id: source.id,
            title: typeof source.title === 'string' && source.title.trim() ? decodeEntities(source.title) : source.id,
            url: safeHttpUrl(source.url),
            published: source.published || null,
            kind: source.kind === 'article' ? 'article' : 'snippet',
            origin: SOURCE_ORIGINS[source.origin] || null,
            window: typeof source.window === 'string' && source.window.trim() ? germanizeText(source.window) : null,
        }));
}

function dayOf(value) {
    return String(value || '').slice(0, 10);
}

function median(values) {
    if (!values.length) return null;
    const sorted = [...values].sort((a, b) => a - b);
    const middle = Math.floor(sorted.length / 2);
    return sorted.length % 2 ? sorted[middle] : (sorted[middle - 1] + sorted[middle]) / 2;
}

/**
 * Trennt Rückblick-Stichtage (je Quartalsende) von laufenden Wochenläufen. Mehrere Läufe an einem Tag
 * zählen als ein Punkt, der letzte gewinnt. Sonst zählte ein wiederholter Lauf im Vergleich doppelt.
 */
export function splitBarometer(points) {
    const history = new Map();
    const live = new Map();
    (Array.isArray(points) ? points : []).forEach((point) => {
        const day = dayOf(point?.as_of);
        const hits = toNumber(point?.hit_count);
        if (!/^\d{4}-\d{2}-\d{2}$/.test(day) || hits === null) return;
        const target = point.run_type === 'history' ? history : live;
        target.set(day, { ...point, as_of: day, hit_count: hits, healthy_count: toNumber(point.healthy_count) });
    });
    const byDay = (a, b) => a.as_of.localeCompare(b.as_of);
    return { history: [...history.values()].sort(byDay), live: [...live.values()].sort(byDay) };
}

const MIN_BAROMETER_UNIVERSE = 200;
const MIN_BAROMETER_HEALTHY_SHARE = 0.25;

/**
 * Trägt der Stichtag eine Aussage über den Markt? Fehlen Geschäftszahlen für den Großteil des Universums,
 * bleiben kaum gesunde Papiere übrig, und die Trefferzahl beschreibt nur den Datenstand. Normal ist rund die
 * Hälfte des Universums gesund.
 */
export function barometerComplete(point) {
    const universe = toNumber(point?.universe_count);
    const healthy = toNumber(point?.healthy_count);
    if (universe === null || healthy === null) return false;
    return universe >= MIN_BAROMETER_UNIVERSE && healthy / universe >= MIN_BAROMETER_HEALTHY_SHARE;
}

function share(point) {
    const healthy = toNumber(point?.healthy_count);
    const hits = toNumber(point?.hit_count);
    return healthy && hits !== null ? hits / healthy : null;
}

/** Mindestzahl an Stichtagen, ab der ein Rang eine Aussage trägt. */
export const MIN_RANK_POINTS = 8;

/**
 * Einordnung über den Rang im Rückblick, ohne festgelegte Skala. Viertel beziehen sich allein auf die
 * eigene Geschichte. Weil das Universum wächst, steht der Anteil an den gesunden Papieren daneben.
 */
export function barometerRank(current, history) {
    const hits = toNumber(current?.hit_count);
    const rows = Array.isArray(history) ? history : [];
    if (hits === null || rows.length < MIN_RANK_POINTS) return null;
    const counts = rows.map((row) => row.hit_count);
    const below = counts.filter((value) => value < hits).length;
    const equal = counts.filter((value) => value === hits).length;
    const above = counts.length - below - equal;
    const position = (below + equal / 2) / counts.length;
    let band = 'mid';
    if (position >= 0.75) band = 'high';
    else if (position <= 0.25) band = 'low';

    const currentShare = share(current);
    const shares = rows.map(share).filter((value) => value !== null);
    return {
        n: counts.length,
        below,
        equal,
        above,
        position,
        band,
        median: median(counts),
        min: Math.min(...counts),
        max: Math.max(...counts),
        firstDay: rows[0].as_of,
        share: currentShare,
        shareMedian: median(shares),
        shareBelow: currentShare === null ? null : shares.filter((value) => value < currentShare).length,
        shareCount: shares.length,
    };
}

/** Bewertungsverlauf in der Kennzahl des Treffers. Nur Stichtage, die auch in den eigenen Median zählen. */
export function valuationPoints(history, measure) {
    const field = YIELD_MEASURES[measure]?.field;
    if (!field || !Array.isArray(history)) return [];
    return history
        .filter((row) => row && row.in_universe !== false)
        .map((row) => ({ x: dayOf(row.as_of), y: toNumber(row[field]) }))
        .filter((point) => point.y !== null && /^\d{4}-\d{2}-\d{2}$/.test(point.x));
}

export function trackPoints(track) {
    if (!track || !Array.isArray(track.points)) return [];
    return track.points
        .map((point) => ({
            x: dayOf(point?.date),
            y: toNumber(point?.change_since_start),
            close: toNumber(point?.price_split_adjusted ?? point?.close_raw),
        }))
        .filter((point) => point.y !== null && /^\d{4}-\d{2}-\d{2}$/.test(point.x));
}

/** Standardfilter blendet verworfene Papiere aus. */
export function matchesRatingFilter(hit, filter) {
    const rating = hit?.my_rating?.rating || null;
    switch (filter) {
        case 'open': return rating !== 'rejected';
        case 'unrated': return rating === null;
        case 'all': return true;
        default: return rating === filter;
    }
}
